/**
 * Shared lead-form logic. Replaces the ~1100 lines of duplicated state,
 * effects, validation, and submit handling that used to live in each
 * per-route form component.
 *
 * Drive variants via `config` (see `src/config/forms.ts`):
 *   - `pickFormId(...)`     — chooses which Beehiiv form ID to POST to
 *   - `tracking`            — which lead events to fire on submit
 *   - `askKnowCoding`       — show/hide the Yes/No coding step
 *   - `showApiExtraQuestions` — hide API-driven extra fields (legacy vk/vaibhav)
 *   - `askExperience`       — show/hide the experience dropdown
 *   - `askYearOfExperience` — show/hide the years-of-experience dropdown
 *   - `extraFields`         — render b2b-style extra UI fields
 *   - `hiddenDefaults`      — silently send these as questionResponses when a
 *                             step is hidden (b2b uses this for professional_label
 *                             and work_experience_range)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { COUNTRIES } from "@/lib/countries";
import { useGeo } from "@/hooks/use-geo";
import { useUtm } from "@/hooks/use-utm";
import { createSubscription } from "@/lib/beehiiv";
import { trackMarketingLead, trackTwitterLead } from "@/lib/marketing-analytics";
import { sendMailmodoEvent } from "@/lib/mailmodo";
import { sendWebengageCTAEvents } from "@/lib/webengage";
import type { FormConfig, KnowCoding } from "@/config/forms";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WHATSAPP_REDIRECT_AFTER_PAINT_MS = 400;
const B2B_WORK_EXPERIENCE_LABEL = "work_experience_range";

export type DynamicAnswer =
  | { type: "text"; value: string }
  | { type: "textarea"; value: string }
  | { type: "choice"; optionId: number; title: string; otherText?: string };

export function isOthersTitle(title: string): boolean {
  return title.trim().toLowerCase() === "others";
}

export function isAnswerValid(q: Question, a: DynamicAnswer | undefined): boolean {
  if (q.questionCategory === "TEXT" || q.questionCategory === "TEXT_AREA") {
    if (!q.isRequired) return true;
    const v = a?.type === "text" || a?.type === "textarea" ? a.value.trim() : "";
    return v.length > 0;
  }
  if (q.questionCategory === "DROPDOWN" || q.questionCategory === "ONE_OF_MANY") {
    if (!q.isRequired) return true;
    if (!a || a.type !== "choice" || a.optionId == null) return false;
    const opt = (q.options ?? []).find((o) => o.optionId === a.optionId);
    if (!opt) return false;
    if (q.allowOthersOption && isOthersTitle(opt.title)) return Boolean(a.otherText?.trim());
    return true;
  }
  return false;
}

function buildDynamicQuestionPayload(
  q: Question,
  a: DynamicAnswer | undefined,
): Record<string, unknown> | null {
  if (!a) return null;
  const base = {
    questionId: q.id,
    formsQuestionIdentityId: q.formsQuestionIdentityId,
    questionType: q.questionType,
    questionCategory: q.questionCategory,
  };
  if (a.type === "text" || a.type === "textarea") {
    const v = a.value.trim();
    return v ? { ...base, responses: [v] } : null;
  }
  if (a.type === "choice") {
    const opt = (q.options ?? []).find((o) => o.optionId === a.optionId);
    if (!opt) return null;
    const responseTitle =
      q.allowOthersOption && isOthersTitle(opt.title) && a.otherText?.trim()
        ? a.otherText.trim()
        : opt.title;
    return { ...base, optionIds: [a.optionId], responses: [responseTitle] };
  }
  return null;
}

function dynamicAnswerToDisplayLine(q: Question, a: DynamicAnswer | undefined): string | null {
  if (!a) return null;
  if (a.type === "text" || a.type === "textarea") {
    const v = a.value.trim();
    return v ? `${q.title}: ${v}` : null;
  }
  if (a.type === "choice") {
    const opt = (q.options ?? []).find((o) => o.optionId === a.optionId);
    if (!opt) return null;
    const title =
      q.allowOthersOption && isOthersTitle(opt.title) && a.otherText?.trim()
        ? a.otherText.trim()
        : opt.title;
    return `${q.title}: ${title}`;
  }
  return null;
}

export const EXPERIENCE_OPTIONS = [
  "Student",
  "Working Professional",
  "Self Employed",
  "Unemployed",
  "Retired",
  "Others",
] as const;

export const YEAR_OF_EXPERIENCE_OPTIONS = [
  "0 years (Fresher)",
  "1-2 years",
  "3-5 years",
  "5-10 years",
  "10-15 years",
  "15+ years",
] as const;

/* -------------------------------------------------------------------------- */
/*  API types — mirrors the shape returned by `${VITE_FORM_API_URL}/forms/X/questionflow` */
/* -------------------------------------------------------------------------- */

export interface QuestionOption {
  optionId: number;
  title: string;
}

export interface Question {
  id: number;
  formsQuestionIdentityId: number;
  title: string;
  subText: string | null;
  placeholderText: string | null;
  questionCategory: string;
  isRequired: boolean;
  allowOthersOption: boolean | null;
  allowConditionality: boolean | null;
  minimumCharactersAllowed: string | null;
  maximumCharactersAllowed: string | null;
  label?: string;
  questionType: string;
  seqNumber: number;
  options?: QuestionOption[];
}

interface StandardQuestionFlow {
  optionId: number;
  followUpQuestions: Question[];
}

export interface QuestionFlowResponse {
  scholarshipQuestions?: Question[];
  exitQuestions?: Question[];
  defaultStandardQuestion?: Question;
  unconditionalQuestions?: Question[];
  standardQuestionsFlow?: StandardQuestionFlow[];
  is_whatsapp_disclaimer?: boolean;
}

type SuccessData = {
  redirectUrl: string;
};

/* -------------------------------------------------------------------------- */
/*  Helpers — pure, no hooks                                                  */
/* -------------------------------------------------------------------------- */

function findQuestionByLabel(
  qf: QuestionFlowResponse | null,
  label: string,
): Question | null {
  if (!qf) return null;
  if (qf.defaultStandardQuestion?.label === label) {
    return qf.defaultStandardQuestion;
  }
  const unconditional = qf.unconditionalQuestions?.find(
    (q) => q.label === label,
  );
  if (unconditional) return unconditional;
  for (const flow of qf.standardQuestionsFlow ?? []) {
    const found = flow.followUpQuestions?.find((q) => q.label === label);
    if (found) return found;
  }
  return null;
}

function findOptionByTitle(
  question: Question | null,
  title: string,
): QuestionOption | null {
  const opts = question?.options ?? [];
  return (
    opts.find((opt) => opt.title.toLowerCase() === title.toLowerCase()) || null
  );
}

/**
 * Locate a Question matching ANY of the given label/title keywords.
 * Used by b2b extra fields ("designation", "company_name", "team_size",
 * "program") where the API question's exact label varies but contains a
 * recognizable token.
 */
function findQuestionByKeyword(
  qf: QuestionFlowResponse | null,
  keywords: readonly string[],
): Question | null {
  if (!qf) return null;
  const all: Question[] = [];
  if (qf.defaultStandardQuestion) all.push(qf.defaultStandardQuestion);
  if (qf.unconditionalQuestions) all.push(...qf.unconditionalQuestions);
  if (qf.standardQuestionsFlow) {
    for (const f of qf.standardQuestionsFlow) {
      if (f.followUpQuestions) all.push(...f.followUpQuestions);
    }
  }
  for (const q of all) {
    const lbl = (q.label || "").toLowerCase();
    const ttl = (q.title || "").toLowerCase();
    for (const kw of keywords) {
      const k = kw.toLowerCase();
      if (lbl === k || lbl.includes(k) || ttl.includes(k)) return q;
    }
  }
  return null;
}

/** UTM source → API `channel` enum ("youtube" | "instagram" | "x"). */
function resolveChannel(utmSource: string | null | undefined): string {
  if (!utmSource) return "youtube";
  const s = utmSource.toLowerCase();
  if (s.includes("instagram") || s === "ig") return "instagram";
  if (s.includes("x") || s.includes("twitter")) return "x";
  if (s.includes("youtube") || s === "yt") return "youtube";
  return "youtube";
}

/* -------------------------------------------------------------------------- */
/*  Hook                                                                      */
/* -------------------------------------------------------------------------- */

export type UseLeadFormProps = {
  config: FormConfig;
  /**
   * Optional PDF asset URL. When set, the success effect downloads it on
   * desktop (anchor click) or opens it in a new tab on mobile. Used by
   * `/ai-roadmap` to deliver the AI Roadmap PDF.
   */
  pdfPath?: string;
};

export function useLeadForm({ config, pdfPath }: UseLeadFormProps) {
  const geo = useGeo();
  const utm = useUtm();

  /* === Static fields shared by every variant === */
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [dialCountryCode, setDialCountryCode] = useState("IN");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [countryEditable, setCountryEditable] = useState(false);

  /* === Step-driven fields (gated by `config.askXxx`) ===
   * For configs that skip a step, the matching `hiddenDefaults` entry seeds the
   * state — so b2b sends "Salaried Professional" / "1-2 years" without ever
   * rendering those fields. */
  const [knowCoding, setKnowCoding] = useState<KnowCoding>("");
  const [experience, setExperience] = useState(
    () => config.hiddenDefaults?.professional_label ?? "",
  );
  const [experienceOptionId, setExperienceOptionId] = useState<number | null>(
    null,
  );
  const [yearOfExperience, setYearOfExperience] = useState(
    () => config.hiddenDefaults?.work_experience_range ?? "",
  );

  /* === Opt-ins === */
  const [enrollMastermind, setEnrollMastermind] = useState(true);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);

  /* === Extra fields (b2b-style) === */
  const [designation, setDesignation] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [teamSize, setTeamSize] = useState(""); // stored as optionId string
  const [programInterest, setProgramInterest] = useState(""); // optionId string

  /* === Dynamic API-driven fields (unconditional + follow-up questions) === */
  const [dynamicAnswers, setDynamicAnswers] = useState<Record<number, DynamicAnswer>>({});

  /* === Dropdown open state === */
  const [experienceOpen, setExperienceOpen] = useState(false);
  const [yearOfExperienceOpen, setYearOfExperienceOpen] = useState(false);
  const [dialCodeOpen, setDialCodeOpen] = useState(false);
  const experienceRef = useRef<HTMLDivElement>(null);
  const yearOfExperienceRef = useRef<HTMLDivElement>(null);
  const dialCodeRef = useRef<HTMLDivElement>(null);
  const dialCodeBtnRef = useRef<HTMLButtonElement>(null);
  const [dialCodePos, setDialCodePos] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  /* === Submission state === */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successData, setSuccessData] = useState<SuccessData | null>(null);
  const twqFiredRef = useRef(false);

  /* === API question flow === */
  const [questionFlow, setQuestionFlow] = useState<QuestionFlowResponse | null>(
    null,
  );

  /* ------------------------------------------------------------------------ */
  /*  Question flow — fetched whenever the form ID changes                    */
  /* ------------------------------------------------------------------------ */
  const currentFormId = useMemo(
    () => config.pickFormId({ knowCoding, countryCode }),
    [config, knowCoding, countryCode],
  );

  useEffect(() => {
    // Only fetch once geo has resolved (so countryCode is a real value, not "").
    // For configs that don't ask knowCoding, fetch immediately when geo loads.
    // For configs that ask knowCoding, wait until the user answers.
    const ready =
      countryCode !== "" &&
      (!config.askKnowCoding || knowCoding !== "");
    if (!ready) return;

    let cancelled = false;
    async function fetchQuestions() {
      try {
        const apiUrl = import.meta.env.VITE_FORM_API_URL;
        const response = await fetch(
          `${apiUrl}/forms/${currentFormId}/questionflow`,
        );
        const data: QuestionFlowResponse | null = response.ok
          ? ((await response.json()) as QuestionFlowResponse)
          : null;
        if (cancelled) return;
        if (data) setQuestionFlow(data);
        else console.warn("⚠️ Failed to fetch question flow");
      } catch (e) {
        if (!cancelled) console.error("❌ Error fetching questions:", e);
      }
    }
    fetchQuestions();
    return () => {
      cancelled = true;
    };
  }, [currentFormId, countryCode, knowCoding, config.askKnowCoding]);


  /* ------------------------------------------------------------------------ */
  /*  Geo → form state sync                                                    */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!geo.loaded) return;
    if (geo.error) {
      setCountryEditable(true);
      setCountry("India");
      setCountryCode("IN");
      return;
    }
    setCountry(geo.country);
    setCountryCode(geo.countryCode);
    if (geo.dialCode) {
      setDialCode(geo.dialCode);
      setDialCountryCode(geo.countryCode);
    }
  }, [geo.loaded, geo.error, geo.country, geo.countryCode, geo.dialCode]);

  /* ------------------------------------------------------------------------ */
  /*  Post-success redirect (short paint delay so spinner is visible)         */
  /*  If `pdfPath` is set, also trigger the PDF download/open before redirect.*/
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!successData) return;

    if (pdfPath) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.open(pdfPath, "_blank", "noopener,noreferrer");
      } else {
        try {
          const link = document.createElement("a");
          link.href = pdfPath;
          link.download = pdfPath.split("/").pop() || "download.pdf";
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (err) {
          console.warn("PDF download failed, opening in new tab:", err);
          window.open(pdfPath, "_blank", "noopener,noreferrer");
        }
      }
    }

    const timer = setTimeout(() => {
      window.location.href = successData.redirectUrl;
    }, WHATSAPP_REDIRECT_AFTER_PAINT_MS);
    return () => clearTimeout(timer);
  }, [successData, pdfPath]);

  /* ------------------------------------------------------------------------ */
  /*  Close dropdowns on outside click                                        */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        experienceRef.current &&
        !experienceRef.current.contains(e.target as Node)
      ) {
        setExperienceOpen(false);
      }
      if (
        yearOfExperienceRef.current &&
        !yearOfExperienceRef.current.contains(e.target as Node)
      ) {
        setYearOfExperienceOpen(false);
      }
      if (
        dialCodeRef.current &&
        !dialCodeRef.current.contains(e.target as Node) &&
        dialCodeBtnRef.current &&
        !dialCodeBtnRef.current.contains(e.target as Node)
      ) {
        setDialCodeOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  /* ------------------------------------------------------------------------ */
  /*  Align selected experience with API-returned optionId                    */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    const opts = questionFlow?.defaultStandardQuestion?.options;
    if (!opts?.length) return;
    if (!experience) {
      setExperienceOptionId(null);
      return;
    }
    const opt = opts.find(
      (o) => o.title.toLowerCase() === experience.toLowerCase(),
    );
    if (!opt) {
      // For user-driven configs, clear stale selection so the UI can re-pick.
      // For configs with a hidden default (b2b), don't reset — the default
      // title may match a different-cased option once the flow loads.
      if (config.askExperience) {
        setExperience("");
        setExperienceOptionId(null);
        setYearOfExperience("");
      }
      return;
    }
    setExperienceOptionId(opt.optionId);
  }, [questionFlow, experience, config.askExperience]);

  /* ------------------------------------------------------------------------ */
  /*  Phone length constraints                                                */
  /* ------------------------------------------------------------------------ */
  const selectedCountry = COUNTRIES.find((c) => c.code === dialCountryCode);
  const phoneMin = selectedCountry
    ? Array.isArray(selectedCountry.phoneLength)
      ? selectedCountry.phoneLength[0]
      : selectedCountry.phoneLength
    : 7;
  const phoneMax = selectedCountry
    ? Array.isArray(selectedCountry.phoneLength)
      ? selectedCountry.phoneLength[1]
      : selectedCountry.phoneLength
    : 15;

  /* ------------------------------------------------------------------------ */
  /*  Resolved b2b extra-field questions (memoised for stable identity)       */
  /* ------------------------------------------------------------------------ */
  const extraFieldQuestions = useMemo(() => {
    return {
      designation: findQuestionByKeyword(questionFlow, [
        "designation",
        "job_title",
        "role",
      ]),
      companyName: findQuestionByKeyword(questionFlow, [
        "company_name",
        "company name",
        "organization",
        "company",
      ]),
      teamSize: findQuestionByKeyword(questionFlow, [
        "team_size",
        "team size",
        "company_size",
      ]),
      programInterest: findQuestionByKeyword(questionFlow, [
        "program",
        "course",
        "interest",
      ]),
    };
  }, [questionFlow]);

  /* ------------------------------------------------------------------------ */
  /*  Dynamic question lists (API-driven)                                     */
  /* ------------------------------------------------------------------------ */
  const sortedUnconditional = useMemo(() => {
    return [...(questionFlow?.unconditionalQuestions ?? [])].sort(
      (a, b) => a.seqNumber - b.seqNumber,
    );
  }, [questionFlow]);

  const activeFollowUps = useMemo((): Question[] => {
    if (!questionFlow?.standardQuestionsFlow || experienceOptionId == null) return [];
    const flow = questionFlow.standardQuestionsFlow.find(
      (f) => f.optionId === experienceOptionId,
    );
    return [...(flow?.followUpQuestions ?? [])].sort((a, b) => a.seqNumber - b.seqNumber);
  }, [questionFlow, experienceOptionId]);

  const visibleFollowUps = useMemo(() => {
    if (config.showApiExtraQuestions === false) return [];
    return activeFollowUps.filter((q) => q.label !== B2B_WORK_EXPERIENCE_LABEL);
  }, [activeFollowUps, config.showApiExtraQuestions]);

  // Exclude unconditional questions already rendered by hardcoded extraFields.
  const unmappedUnconditional = useMemo(() => {
    if (config.showApiExtraQuestions === false) return [];
    const mappedIds = new Set(
      Object.values(extraFieldQuestions)
        .filter((q): q is Question => q !== null)
        .map((q) => q.formsQuestionIdentityId),
    );
    return sortedUnconditional.filter((q) => !mappedIds.has(q.formsQuestionIdentityId));
  }, [sortedUnconditional, extraFieldQuestions, config.showApiExtraQuestions]);

  // Auto-seed hidden work_experience_range answer; drop stale follow-up answers.
  useEffect(() => {
    if (!questionFlow?.standardQuestionsFlow || experienceOptionId == null) return;
    const currentFlow = questionFlow.standardQuestionsFlow.find(
      (f) => f.optionId === experienceOptionId,
    );
    const currentIds = new Set(
      currentFlow?.followUpQuestions?.map((q) => q.formsQuestionIdentityId) ?? [],
    );
    const allFollowIds = new Set<number>();
    for (const f of questionFlow.standardQuestionsFlow) {
      for (const q of f.followUpQuestions ?? []) allFollowIds.add(q.formsQuestionIdentityId);
    }
    const wq = currentFlow?.followUpQuestions?.find(
      (q) => q.label === B2B_WORK_EXPERIENCE_LABEL,
    );
    const opts = wq?.options ?? [];
    const defaultTitle = config.hiddenDefaults?.work_experience_range ?? "";
    const opt =
      opts.find((o) => o.title.toLowerCase() === defaultTitle.toLowerCase()) ?? opts[0];

    setDynamicAnswers((prev) => {
      const next = { ...prev };
      for (const id of allFollowIds) {
        if (!currentIds.has(id)) delete next[id];
      }
      if (wq && opt) {
        next[wq.formsQuestionIdentityId] = { type: "choice", optionId: opt.optionId, title: opt.title };
      }
      return next;
    });
  }, [experienceOptionId, questionFlow, config.hiddenDefaults]);

  const updateDynamicAnswer = useCallback(
    (identityId: number, next: DynamicAnswer | undefined) => {
      setDynamicAnswers((prev) => {
        if (next === undefined) {
          const rest = { ...prev };
          delete rest[identityId];
          return rest;
        }
        return { ...prev, [identityId]: next };
      });
    },
    [],
  );

  /* ------------------------------------------------------------------------ */
  /*  Country picker (manual override)                                        */
  /* ------------------------------------------------------------------------ */
  const handleCountryChange = useCallback(
    (code: string) => {
      const match = COUNTRIES.find((c) => c.code === code);
      if (!match) return;
      setCountry(match.name);
      setCountryCode(match.code);
      setDialCode(match.dialCode);
      setDialCountryCode(match.code);
      const max = Array.isArray(match.phoneLength)
        ? match.phoneLength[1]
        : match.phoneLength;
      if (phone.length > max) setPhone(phone.slice(0, max));
    },
    [phone],
  );

  /* ------------------------------------------------------------------------ */
  /*  Per-field validation                                                    */
  /* ------------------------------------------------------------------------ */
  const isFieldValid = useCallback(
    (field: string): boolean => {
      switch (field) {
        case "name":
          return name.trim().length >= 2;
        case "email":
          return EMAIL_RE.test(email.trim());
        case "phone":
          return phone.length >= phoneMin && phone.length <= phoneMax;
        case "knowCoding":
          return knowCoding === "yes" || knowCoding === "no";
        case "experience":
          return experience !== "";
        case "yearOfExperience":
          return yearOfExperience !== "";
        case "designation":
          return designation.trim().length > 0;
        case "companyName":
          return companyName.trim().length > 0;
        case "teamSize":
          return teamSize.trim().length > 0;
        case "programInterest":
          return programInterest.trim().length > 0;
        default:
          return false;
      }
    },
    [
      name,
      email,
      phone,
      phoneMin,
      phoneMax,
      knowCoding,
      experience,
      yearOfExperience,
      designation,
      companyName,
      teamSize,
      programInterest,
    ],
  );

  const isFormComplete = useMemo(() => {
    if (!geo.loaded) return false;
    if (!isFieldValid("name")) return false;
    if (!isFieldValid("email")) return false;
    if (!isFieldValid("phone")) return false;
    if (config.askKnowCoding && !isFieldValid("knowCoding")) return false;
    if (config.askExperience && !isFieldValid("experience")) return false;
    if (
      config.askYearOfExperience &&
      experience !== "Student" &&
      !isFieldValid("yearOfExperience")
    ) {
      return false;
    }
    // Extra fields — every listed extra is treated as required EXCEPT
    // `designation`, which is optional in b2b (matches legacy behavior).
    if (config.extraFields?.includes("companyName") && !isFieldValid("companyName")) {
      return false;
    }
    if (config.extraFields?.includes("teamSize") && !isFieldValid("teamSize")) {
      return false;
    }
    if (
      config.extraFields?.includes("programInterest") &&
      !isFieldValid("programInterest")
    ) {
      return false;
    }
    // Dynamic API-driven fields (only when extra API questions are shown in UI)
    if (config.showApiExtraQuestions !== false) {
      const allDynamic = [...unmappedUnconditional, ...activeFollowUps];
      for (const q of allDynamic) {
        if (!isAnswerValid(q, dynamicAnswers[q.formsQuestionIdentityId])) return false;
      }
    }
    return true;
  }, [geo.loaded, isFieldValid, config, experience, unmappedUnconditional, activeFollowUps, dynamicAnswers]);

  /* ------------------------------------------------------------------------ */
  /*  Full validate (sets fieldErrors and returns boolean)                    */
  /* ------------------------------------------------------------------------ */
  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!name.trim()) errors.name = "Name is required.";
    else if (name.trim().length < 2)
      errors.name = "Name must be at least 2 characters.";

    if (!email.trim()) errors.email = "Email is required.";
    else if (!EMAIL_RE.test(email.trim()))
      errors.email = "Please enter a valid email address.";

    if (!phone.trim()) errors.phone = "Phone number is required.";
    else if (phone.length < phoneMin || phone.length > phoneMax) {
      errors.phone =
        phoneMin === phoneMax
          ? `Phone number must be ${phoneMin} digits.`
          : `Phone number must be ${phoneMin}–${phoneMax} digits.`;
    }

    if (config.askKnowCoding && knowCoding !== "yes" && knowCoding !== "no") {
      errors.knowCoding = "Please select whether you know coding.";
    }

    if (config.askExperience && !experience) {
      errors.experience = "Please select what describes you the best.";
    }

    if (
      config.askYearOfExperience &&
      experience &&
      experience !== "Student" &&
      !yearOfExperience
    ) {
      errors.yearOfExperience = "Please select years of experience.";
    }

    if (config.extraFields?.includes("companyName") && !companyName.trim()) {
      errors.companyName = "Company name is required.";
    }
    if (config.extraFields?.includes("teamSize") && !teamSize.trim()) {
      errors.teamSize = "Please select team size.";
    }
    if (
      config.extraFields?.includes("programInterest") &&
      !programInterest.trim()
    ) {
      errors.programInterest = "Please select a program.";
    }

    // Dynamic API-driven fields (only when extra API questions are shown in UI)
    if (questionFlow && config.showApiExtraQuestions !== false) {
      for (const q of unmappedUnconditional) {
        if (!isAnswerValid(q, dynamicAnswers[q.formsQuestionIdentityId])) {
          errors[`dyn-${q.formsQuestionIdentityId}`] = q.isRequired
            ? `${q.title} is required.`
            : `Please check ${q.title}.`;
        }
      }
      for (const q of activeFollowUps) {
        if (q.label === B2B_WORK_EXPERIENCE_LABEL) continue;
        if (!isAnswerValid(q, dynamicAnswers[q.formsQuestionIdentityId])) {
          errors[`dyn-${q.formsQuestionIdentityId}`] = q.isRequired
            ? `${q.title} is required.`
            : `Please check ${q.title}.`;
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [
    name,
    email,
    phone,
    phoneMin,
    phoneMax,
    knowCoding,
    experience,
    yearOfExperience,
    companyName,
    teamSize,
    programInterest,
    config,
    questionFlow,
    unmappedUnconditional,
    activeFollowUps,
    dynamicAnswers,
  ]);

  /* ------------------------------------------------------------------------ */
  /*  Submit                                                                  */
  /* ------------------------------------------------------------------------ */
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      setError("");
      setFieldErrors({});

      if (!validate()) return;

      (document.activeElement as HTMLElement | null)?.blur();

      // Tracking — fire whatever the config requests.
      if (config.tracking.trackMarketingLead) trackMarketingLead();
      if (config.tracking.fbqLead) window.fbq?.("track", "Lead");
      if (config.tracking.gtagEvent) window.gtag?.("event", config.tracking.gtagEvent);

      setLoading(true);
      try {
        const formId = config.pickFormId({ knowCoding, countryCode });
        const channel = resolveChannel(utm.utmSource);

        const mailmodoEmail = email.trim().toLowerCase();
        const mailmodoProps = {
          form_id: formId,
          email: mailmodoEmail,
          name: name.trim(),
          phone: `${dialCode}${phone}`,
        };
        sendMailmodoEvent(mailmodoEmail, "Application Form Viewed", mailmodoProps);
        sendMailmodoEvent(mailmodoEmail, "Application Form Started", mailmodoProps);

        // Build questionResponses ---------------------------------------------------
        const questionResponses: Array<Record<string, unknown>> = [];
        let experienceLabelForPayload = experience;

        if (questionFlow) {
          // professional_label / work_experience question
          const expQuestion =
            findQuestionByLabel(questionFlow, "professional_label") ||
            findQuestionByLabel(questionFlow, "work_experience");

          // Hidden default for professional_label (used by b2b)
          const hiddenExperienceTitle =
            config.hiddenDefaults?.professional_label;
          const effectiveExperience = experience || hiddenExperienceTitle || "";

          if (expQuestion && effectiveExperience) {
            const expOpts = expQuestion.options ?? [];
            const expOptionId =
              experienceOptionId ??
              findOptionByTitle(expQuestion, effectiveExperience)?.optionId;
            const expOption =
              expOptionId != null
                ? expOpts.find((o) => o.optionId === expOptionId) ??
                  findOptionByTitle(expQuestion, effectiveExperience)
                : findOptionByTitle(expQuestion, effectiveExperience);
            if (expOption) {
              experienceLabelForPayload = expOption.title;
              const isDefaultStandard =
                expQuestion.questionType === "DEFAULT_STANDARD_QUESTION";
              questionResponses.push({
                responses: [expOption.title],
                questionId: expQuestion.id,
                formsQuestionIdentityId: expQuestion.formsQuestionIdentityId,
                questionType: expQuestion.questionType,
                questionCategory: expQuestion.questionCategory,
                ...(isDefaultStandard ? {} : { optionIds: [expOption.optionId] }),
              });
            }
          }

          // work_experience_range
          const yearExpQuestion = findQuestionByLabel(
            questionFlow,
            "work_experience_range",
          );
          const hiddenYearTitle = config.hiddenDefaults?.work_experience_range;
          const effectiveYearTitle = yearOfExperience || hiddenYearTitle || "";
          if (yearExpQuestion && effectiveYearTitle) {
            const yearExpOption = findOptionByTitle(
              yearExpQuestion,
              effectiveYearTitle,
            );
            if (yearExpOption) {
              questionResponses.push({
                optionIds: [yearExpOption.optionId],
                responses: [yearExpOption.title],
                questionId: yearExpQuestion.id,
                formsQuestionIdentityId: yearExpQuestion.formsQuestionIdentityId,
                questionType: yearExpQuestion.questionType,
                questionCategory: yearExpQuestion.questionCategory,
              });
            }
          }

          // ---- Extra fields (b2b) ----
          if (config.extraFields?.includes("designation")) {
            const q = extraFieldQuestions.designation;
            if (q && designation.trim()) {
              questionResponses.push({
                responses: [designation.trim()],
                questionId: q.id,
                formsQuestionIdentityId: q.formsQuestionIdentityId,
                questionType: q.questionType,
                questionCategory: q.questionCategory,
              });
            }
          }
          if (config.extraFields?.includes("companyName")) {
            const q = extraFieldQuestions.companyName;
            if (q && companyName.trim()) {
              questionResponses.push({
                responses: [companyName.trim()],
                questionId: q.id,
                formsQuestionIdentityId: q.formsQuestionIdentityId,
                questionType: q.questionType,
                questionCategory: q.questionCategory,
              });
            }
          }
          if (config.extraFields?.includes("teamSize")) {
            const q = extraFieldQuestions.teamSize;
            if (q && teamSize) {
              const oid = Number(teamSize);
              const opt = q.options?.find((o) => o.optionId === oid);
              if (opt) {
                questionResponses.push({
                  optionIds: [opt.optionId],
                  responses: [opt.title],
                  questionId: q.id,
                  formsQuestionIdentityId: q.formsQuestionIdentityId,
                  questionType: q.questionType,
                  questionCategory: q.questionCategory,
                });
              }
            }
          }
          if (config.extraFields?.includes("programInterest")) {
            const q = extraFieldQuestions.programInterest;
            if (q && programInterest) {
              const oid = Number(programInterest);
              const opt = q.options?.find((o) => o.optionId === oid);
              if (opt) {
                questionResponses.push({
                  optionIds: [opt.optionId],
                  responses: [opt.title],
                  questionId: q.id,
                  formsQuestionIdentityId: q.formsQuestionIdentityId,
                  questionType: q.questionType,
                  questionCategory: q.questionCategory,
                });
              }
            }
          }

          // Dynamic API-driven questions (routes with showApiExtraQuestions only)
          if (config.showApiExtraQuestions !== false) {
            for (const q of unmappedUnconditional) {
              const p = buildDynamicQuestionPayload(q, dynamicAnswers[q.formsQuestionIdentityId]);
              if (p) questionResponses.push(p);
            }
            for (const q of activeFollowUps) {
              const p = buildDynamicQuestionPayload(q, dynamicAnswers[q.formsQuestionIdentityId]);
              if (p) questionResponses.push(p);
            }
          }
        }

        // Sidecar lines for Slack/Sheet (b2b passes these explicitly today)
        const dynamicFieldLines: string[] = [];
        if (designation.trim())
          dynamicFieldLines.push(`Designation: ${designation.trim()}`);
        if (companyName.trim())
          dynamicFieldLines.push(`Company Name: ${companyName.trim()}`);
        if (teamSize && extraFieldQuestions.teamSize) {
          const oid = Number(teamSize);
          const opt = extraFieldQuestions.teamSize.options?.find(
            (o) => o.optionId === oid,
          );
          if (opt) dynamicFieldLines.push(`Team Size: ${opt.title}`);
        }
        if (programInterest && extraFieldQuestions.programInterest) {
          const oid = Number(programInterest);
          const opt = extraFieldQuestions.programInterest.options?.find(
            (o) => o.optionId === oid,
          );
          if (opt) dynamicFieldLines.push(`Program: ${opt.title}`);
        }
        if (config.showApiExtraQuestions !== false) {
          for (const q of unmappedUnconditional) {
            const line = dynamicAnswerToDisplayLine(q, dynamicAnswers[q.formsQuestionIdentityId]);
            if (line) dynamicFieldLines.push(line);
          }
          for (const q of visibleFollowUps) {
            const line = dynamicAnswerToDisplayLine(q, dynamicAnswers[q.formsQuestionIdentityId]);
            if (line) dynamicFieldLines.push(line);
          }
        }

        const apiUrl = import.meta.env.VITE_FORM_API_URL;
        await sendWebengageCTAEvents({
          form_id: formId,
          email: mailmodoEmail,
          name: name.trim(),
          phone: `${dialCode}${phone.trim()}`,
        });

        const payload = {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: `${dialCode}${phone.trim()}`,
          channel,
          country,
          countryCode,
          dialCode,
          formId,
          source: "stayingahead.club",
          subscribeNewsletter,
          enrollMastermind,
          ...(dynamicFieldLines.length > 0 && { dynamicFieldLines }),
          utm_source: utm.utmSource || undefined,
          utm_medium: utm.utmMedium || undefined,
          utm_campaign: utm.utmCampaign || undefined,
          utm_content: utm.utmContent || undefined,
          utm_term: utm.utmTerm || undefined,
          utm_adset: utm.utmAdset || undefined,
          campaign_id: utm.campaignId
            ? parseInt(utm.campaignId) || undefined
            : undefined,
          questions: {
            experience: experienceLabelForPayload,
            yearOfExperience,
            ...(config.askKnowCoding ? { knowCoding } : {}),
          },
          ...(questionResponses.length > 0 && { questionResponses }),
        };

        const response = await fetch(`${apiUrl}/api-forms`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_FORMS_API_TOKEN}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok || data.status === "error") {
          console.error("❌ API error:", data);
          setError(data.message || "Something went wrong. Please try again.");
          return;
        }

        await sendMailmodoEvent(mailmodoEmail, "Application Form Completed", mailmodoProps);

        if (config.tracking.twqLead && !twqFiredRef.current) {
          twqFiredRef.current = true;
          trackTwitterLead();
        }

        setSuccessData({
          redirectUrl: data.redirect_url || "#",
        });

        if (subscribeNewsletter) {
          createSubscription({
            email: email.trim().toLowerCase(),
            send_welcome_email: false,
            reactivate_existing: false,
            utm_source: utm.utmSource || undefined,
            utm_medium: utm.utmMedium || undefined,
            utm_campaign: utm.utmCampaign || undefined,
            utm_term: utm.utmTerm || undefined,
            utm_content: utm.utmContent || undefined,
            referring_site: "https://stayingahead.club",
            custom_fields: name.trim()
              ? [{ name: "Name", value: name.trim() }]
              : undefined,
          }).catch((err) => console.warn("Beehiiv subscription:", err));
        }
      } catch (err) {
        console.error("Submission error:", err);
        setError("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [
      validate,
      config,
      knowCoding,
      countryCode,
      utm,
      questionFlow,
      experience,
      experienceOptionId,
      yearOfExperience,
      name,
      email,
      phone,
      dialCode,
      country,
      designation,
      companyName,
      teamSize,
      programInterest,
      extraFieldQuestions,
      enrollMastermind,
      subscribeNewsletter,
      unmappedUnconditional,
      activeFollowUps,
      visibleFollowUps,
      dynamicAnswers,
    ],
  );

  return {
    /* config (consumer reads flags to render conditional UI) */
    config,

    /* hooks */
    geo,
    utm,

    /* static field state */
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    dialCode,
    setDialCode,
    dialCountryCode,
    setDialCountryCode,
    country,
    setCountry,
    countryCode,
    setCountryCode,
    countryEditable,
    setCountryEditable,

    /* step state */
    knowCoding,
    setKnowCoding,
    experience,
    setExperience,
    experienceOptionId,
    setExperienceOptionId,
    yearOfExperience,
    setYearOfExperience,

    /* opt-ins */
    enrollMastermind,
    setEnrollMastermind,
    subscribeNewsletter,
    setSubscribeNewsletter,

    /* extra fields (b2b) */
    designation,
    setDesignation,
    companyName,
    setCompanyName,
    teamSize,
    setTeamSize,
    programInterest,
    setProgramInterest,
    extraFieldQuestions,

    /* dropdown UI state */
    experienceOpen,
    setExperienceOpen,
    yearOfExperienceOpen,
    setYearOfExperienceOpen,
    dialCodeOpen,
    setDialCodeOpen,
    experienceRef,
    yearOfExperienceRef,
    dialCodeRef,
    dialCodeBtnRef,
    dialCodePos,
    setDialCodePos,

    /* submission state */
    loading,
    error,
    fieldErrors,
    successData,

    /* derived */
    phoneMin,
    phoneMax,
    isFormComplete,
    professionalExperienceOptions:
      questionFlow?.defaultStandardQuestion?.options ?? null,

    /* dynamic API-driven questions */
    unmappedUnconditional,
    visibleFollowUps,
    dynamicAnswers,
    updateDynamicAnswer,

    /* handlers */
    handleCountryChange,
    isFieldValid,
    handleSubmit,
  };
}

export type LeadFormApi = ReturnType<typeof useLeadForm>;
